import {
  Box,
  IconButton,
  Paper,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyToClipboard } from '../../../components/CopyToClipboard';
import { LinkedCurations } from '../../../components/Curation/Popovers';
import { FeaturedCardType, IFeaturedCard } from '../../../services/types';
import { convertFirstCharacterToUppercase } from '../../../services/utils';
import useStyles from './styles';

interface IProps {
  data: IFeaturedCard[] | undefined;
  totalCount: number | undefined;
  page: number;
  onRequestFetch: (page: number, rowsPerPage: number) => void;
  onClickDeleteButton: (featuredCard: IFeaturedCard) => void;
  onClickEdit: (featuredCard: IFeaturedCard) => void;
  getLinkUri?: (trackingId: string | undefined) => string;
}

export const FeaturedCardList = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const [rowsPerPage, setRowsPerPage] = useState<number>(20);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorData, setAnchorData] = React.useState<null | IFeaturedCard>(null);

  const handleLinkedClick = (event: React.MouseEvent<HTMLButtonElement>, fc: IFeaturedCard) => {
    event.stopPropagation();
    event.preventDefault();
    if (fc.linkedCurations && fc.linkedCurations.length > 0) {
      setAnchorEl(event.currentTarget);
      setAnchorData(fc);
    }
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
    setAnchorData(null);
  };

  if (props.data && props.data.length === 0) {
    return (
      <Paper>
        <Box display="flex" justifyContent="center" alignItems="center" className={classes.noDataBox} flexDirection="column">
          <Typography>No data</Typography>
          <Typography variant="caption">{'Tap the "Create Featured Card Set" at the top to create a first featured card set.'}</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <div className={classes.root}>
      <Paper>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell align="center" style={{ width: 48 }}>
                Linked
              </TableCell>
              <TableCell align="center" style={{ width: 100 }}>
                ID
              </TableCell>
              <TableCell align="center" style={{ width: 110 }}>
                Cover
              </TableCell>
              <TableCell align="center">Featured Card Title</TableCell>
              <TableCell align="center">Countries</TableCell>
              <TableCell align="center">Content Type</TableCell>
              <TableCell align="center">Type</TableCell>
              <TableCell align="center" style={{ width: 210 }}>
                Created at
              </TableCell>
              <TableCell align="center" style={{ width: 75 }}>
                Delete
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.data &&
              props.data.length > 0 &&
              props.data.map(fc => {
                return (
                  <TableRow
                    component={Link}
                    to={'#'}
                    hover
                    onClick={() => {
                      if (anchorEl === null) {
                        props.onClickEdit?.(fc);
                      }
                    }}
                    key={fc.id}
                    className={classes.featuredCardSetRow}
                  >
                    <TableCell align="center">
                      <span
                        className={classes.linkCountText}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          handleLinkedClick(e, fc);
                        }}
                      >
                        {fc.linkedCurations?.length || 0}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <CopyToClipboard content={fc.id!.toString()} />
                    </TableCell>
                    <TableCell align="center">
                      <div
                        style={{ backgroundImage: `url(${process.env.CDN_URL}/${fc.displayCoverUrl || fc.coverUrl}?w=320)` }}
                        className={classes.featuredCardListCoverImage}
                      />
                    </TableCell>
                    <TableCell align="left">{fc.title}</TableCell>
                    <TableCell align="center">{fc.availableCountryCountText}</TableCell>
                    <TableCell align="center" className={classes.capital}>
                      {(fc.type === FeaturedCardType.Story && fc.storyTypes) ||
                        (fc.type === FeaturedCardType.Season && fc.storyTypes) ||
                        (fc.type === FeaturedCardType.Collection &&
                          fc.storyTypes?.map(storyType => convertFirstCharacterToUppercase(storyType)).join(', ')) ||
                        '-'}
                    </TableCell>
                    <TableCell align="center" className={classes.capital}>
                      {fc.type}
                    </TableCell>
                    <TableCell align="center">{moment(fc.createdAt).format('lll')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={false ? 'You cannot delete it in the active state.' : ''} placement="left-end">
                        <IconButton
                          style={{
                            opacity: false ? 0.25 : 1,
                          }}
                          color={false ? 'default' : 'primary'}
                          disableFocusRipple={false}
                          disableTouchRipple={false}
                          disableRipple={false}
                          onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            props.onClickDeleteButton(fc);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        {props.totalCount !== undefined && (
          <TablePagination
            count={props.totalCount}
            rowsPerPage={rowsPerPage}
            page={props.page}
            onChangePage={(e: unknown, v: number) => {
              props.onRequestFetch(v, rowsPerPage);
            }}
            onChangeRowsPerPage={(event: React.ChangeEvent<HTMLInputElement>) => {
              const rpp = parseInt(event.target.value, 10);
              setRowsPerPage(rpp);
              props.onRequestFetch(0, rpp);
            }}
          />
        )}
        {anchorData && (
          <Popover
            id={`children-popover-${anchorData.id}`}
            open={true}
            anchorEl={anchorEl}
            onClose={handlePopoverClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <LinkedCurations linkedCurations={anchorData.linkedCurations!} type="featuredcard" />
          </Popover>
        )}
      </Paper>
    </div>
  );
};
